// Meeting Tools panel (new module). A side panel like People/Chat/Files where
// in-meeting tools live. Nested navigation: a tool list, then a per-tool detail
// view (back-arrow returns to the list), mirroring the design. `view` is
// panel-local state; nothing here is on the video render path, so opening a
// tool or updating one never re-renders a single tile.
//
// Tools are a registry so future tools slot in without touching the shell.
// Record and Timer are host-controlled: attendees still SEE them in the list
// but disabled (a lock, not a chevron), so it's clear they're host powers.
// Polls stay interactive for everyone — that's where attendees vote.

import { memo, ReactNode, useCallback, useState } from 'react';

import { probeRender } from '../perf/renderProbe';
import { useCallStore } from '../store/callStore';
import { IconButton } from '../ui/IconButton';
import {
  IconChevronLeft,
  IconChevronRight,
  IconClose,
  IconLock,
  IconNote,
  IconPoll,
  IconRecord,
  IconTimer,
} from '../ui/icons';
import { NotesTool } from './tools/NotesTool';
import { PollsTool } from './tools/PollsTool';
import { RecordTool } from './tools/RecordTool';
import { TimerTool } from './tools/TimerTool';

type ToolId = 'notes' | 'record' | 'polls' | 'timer';

interface Tool {
  id: ToolId;
  title: string;
  subtitle: string;
  icon: ReactNode;
  /** Host controls it; attendees see it disabled (Record, Timer). Polls = false
   * (everyone interacts — attendees vote). */
  hostControlled: boolean;
  render: () => ReactNode;
}

const TOOLS: Tool[] = [
  {
    id: 'notes',
    title: 'Notes',
    subtitle: 'Shared meeting notes',
    icon: <IconNote size={19} />,
    hostControlled: false,
    render: () => <NotesTool />,
  },
  {
    id: 'record',
    title: 'Record',
    subtitle: 'Capture the meeting',
    icon: <IconRecord size={19} />,
    hostControlled: true,
    render: () => <RecordTool />,
  },
  {
    id: 'polls',
    title: 'Polls',
    subtitle: 'Ask the room a question',
    icon: <IconPoll size={19} />,
    hostControlled: false,
    render: () => <PollsTool />,
  },
  {
    id: 'timer',
    title: 'Timer',
    subtitle: 'Show a countdown timer',
    icon: <IconTimer size={19} />,
    hostControlled: true,
    render: () => <TimerTool />,
  },
];

export const MeetingToolsPanel = memo(function MeetingToolsPanel({
  onClose,
}: {
  onClose: () => void;
}) {
  probeRender('MeetingToolsPanel');
  const isHost = useCallStore((s) => s.isHost);
  const pollsAllowed = useCallStore((s) => s.pollsAllowed);
  const timerAllowed = useCallStore((s) => s.timerAllowed);
  const notesAllowed = useCallStore((s) => s.notesAllowed);
  const [view, setView] = useState<ToolId | null>(null);

  // Org policy hides a tool outright; host-controlled tools are never hidden —
  // attendees see them disabled.
  const tools = TOOLS.filter((t) => {
    if (t.id === 'polls' && !pollsAllowed) return false;
    if (t.id === 'timer' && !timerAllowed) return false;
    if (t.id === 'notes' && !notesAllowed) return false;
    return true;
  });
  const locked = useCallback((t: Tool) => t.hostControlled && !isHost, [isHost]);
  const active = tools.find((t) => t.id === view && !locked(t)) ?? null;

  const openTool = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    setView(e.currentTarget.dataset.tool as ToolId);
  }, []);
  const back = useCallback(() => setView(null), []);

  return (
    <div className="bz-panel bz-mt-panel" role="region" aria-label="Meeting tools">
      <div className="bz-panel-header">
        {active ? (
          <button type="button" className="bz-mt-back" onClick={back} aria-label="Back to tools">
            <IconChevronLeft size={18} />
            <span>{active.title}</span>
          </button>
        ) : (
          <span>Meeting tools</span>
        )}
        <IconButton shape="flat" label="Close" onClick={onClose}>
          <IconClose size={14} />
        </IconButton>
      </div>

      {active ? (
        <div className="bz-mt-detail">{active.render()}</div>
      ) : (
        <div className="bz-mt-list">
          {tools.length === 0 ? (
            <p className="bz-mt-empty">No meeting tools are available right now.</p>
          ) : (
            tools.map((t) => {
              const isLocked = locked(t);
              return (
                <button
                  key={t.id}
                  type="button"
                  className="bz-mt-tool"
                  data-tool={t.id}
                  onClick={openTool}
                  disabled={isLocked}
                >
                  <span className="bz-mt-tool-icon">{t.icon}</span>
                  <span className="bz-mt-tool-text">
                    <span className="bz-mt-tool-title">{t.title}</span>
                    <span className="bz-mt-tool-sub">{isLocked ? 'Host only' : t.subtitle}</span>
                  </span>
                  {isLocked ? <IconLock size={15} /> : <IconChevronRight size={18} />}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
});
